import type { Point, Rect, Ellipse, Line, Shape, LayoutNode } from '../types';

/** Check if two rects overlap (exclusive — touching edges don't count) */
export function rectIntersects(a: Rect, b: Rect): boolean {
	return (
		a.x < b.x + b.width &&
		a.x + a.width > b.x &&
		a.y < b.y + b.height &&
		a.y + a.height > b.y
	);
}

/** Check if rect `a` fully contains rect `b` */
export function rectContains(a: Rect, b: Rect): boolean {
	return (
		b.x >= a.x &&
		b.y >= a.y &&
		b.x + b.width <= a.x + a.width &&
		b.y + b.height <= a.y + a.height
	);
}

/** Euclidean distance between two points */
export function distance(a: Point, b: Point): number {
	const dx = b.x - a.x;
	const dy = b.y - a.y;
	return Math.sqrt(dx * dx + dy * dy);
}

/** Centroid of a set of points */
export function centroid(points: Point[]): Point {
	if (points.length === 0) return { x: 0, y: 0 };
	let sx = 0;
	let sy = 0;
	for (const p of points) {
		sx += p.x;
		sy += p.y;
	}
	return { x: sx / points.length, y: sy / points.length };
}

/** Bounding rect of a set of rects */
export function boundingRect(rects: Rect[]): Rect {
	if (rects.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const r of rects) {
		minX = Math.min(minX, r.x);
		minY = Math.min(minY, r.y);
		maxX = Math.max(maxX, r.x + r.width);
		maxY = Math.max(maxY, r.y + r.height);
	}
	return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Get bounding rect of a shape */
export function shapeBounds(shape: Shape): Rect {
	if (shape.type === 'rect') return shape.bounds;
	const { cx, cy, rx, ry } = shape.ellipse;
	return { x: cx - rx, y: cy - ry, width: rx * 2, height: ry * 2 };
}

/** Get center point of a shape */
export function shapeCenter(shape: Shape): Point {
	if (shape.type === 'rect') {
		return {
			x: shape.bounds.x + shape.bounds.width / 2,
			y: shape.bounds.y + shape.bounds.height / 2,
		};
	}
	return { x: shape.ellipse.cx, y: shape.ellipse.cy };
}

/** Center point of a rect */
export function rectCenter(r: Rect): Point {
	return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
}

/** Line-line intersection. Returns intersection point or null. */
export function lineIntersection(a: Line, b: Line): Point | null {
	const d1x = a.x2 - a.x1;
	const d1y = a.y2 - a.y1;
	const d2x = b.x2 - b.x1;
	const d2y = b.y2 - b.y1;

	const denom = d1x * d2y - d1y * d2x;
	if (Math.abs(denom) < 1e-10) return null; // parallel

	const t = ((b.x1 - a.x1) * d2y - (b.y1 - a.y1) * d2x) / denom;
	const u = ((b.x1 - a.x1) * d1y - (b.y1 - a.y1) * d1x) / denom;

	if (t < 0 || t > 1 || u < 0 || u > 1) return null;

	return {
		x: a.x1 + t * d1x,
		y: a.y1 + t * d1y,
	};
}

/** Check if a line segment intersects a rect */
export function lineRectIntersects(line: Line, rect: Rect): boolean {
	// Check if either endpoint is inside
	if (
		line.x1 >= rect.x &&
		line.x1 <= rect.x + rect.width &&
		line.y1 >= rect.y &&
		line.y1 <= rect.y + rect.height
	) {
		return true;
	}
	if (
		line.x2 >= rect.x &&
		line.x2 <= rect.x + rect.width &&
		line.y2 >= rect.y &&
		line.y2 <= rect.y + rect.height
	) {
		return true;
	}

	// Check against all 4 edges
	const edges: Line[] = [
		{ x1: rect.x, y1: rect.y, x2: rect.x + rect.width, y2: rect.y },
		{
			x1: rect.x + rect.width,
			y1: rect.y,
			x2: rect.x + rect.width,
			y2: rect.y + rect.height,
		},
		{
			x1: rect.x,
			y1: rect.y + rect.height,
			x2: rect.x + rect.width,
			y2: rect.y + rect.height,
		},
		{ x1: rect.x, y1: rect.y, x2: rect.x, y2: rect.y + rect.height },
	];

	for (const edge of edges) {
		if (lineIntersection(line, edge) !== null) return true;
	}

	return false;
}

/** Get bounding rects for all layout nodes */
export function nodeBounds(nodes: LayoutNode[]): Rect[] {
	return nodes.map((n) => shapeBounds(n.shape));
}

/** Centroid of layout nodes (based on shape centers) */
export function nodeCentroid(nodes: LayoutNode[]): Point {
	return centroid(nodes.map((n) => shapeCenter(n.shape)));
}

/** Area of a shape's bounding rect */
export function shapeArea(shape: Shape): number {
	const b = shapeBounds(shape);
	return b.width * b.height;
}
